import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { useBillingStats } from '../../hooks/useBillingStats';
import { useTrialStatus } from '../../hooks/useTrialStatus';

const BillingDashboard: React.FC = () => {
  const { billingStats, loading, error } = useBillingStats();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Errore nel caricamento delle statistiche di billing: {error.message}
      </Alert>
    );
  }

  if (!billingStats) {
    return <Alert severity="info">Nessuna statistica disponibile</Alert>;
  }

  const stats = [
    {
      title: 'Utenti Totali',
      value: billingStats.totalUsers,
      color: '#3b82f6',
    },
    {
      title: 'In Trial',
      value: billingStats.trialUsers,
      color: '#f59e0b',
    },
    {
      title: 'Attivi',
      value: billingStats.activeUsers,
      color: '#10b981',
    },
    {
      title: 'Frozen',
      value: billingStats.frozenUsers,
      color: '#ef4444',
    },
    {
      title: 'Expired Trials',
      value: billingStats.expiredTrials,
      color: '#dc2626',
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard Billing
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat) => (
          <Grid item xs={12} sm={6} md={2.4} key={stat.title}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {stat.title}
                </Typography>
                <Typography
                  variant="h4"
                  component="div"
                  sx={{ color: stat.color }}
                >
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Statistiche Dettagliate
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Metrica</TableCell>
                  <TableCell align="right">Valore</TableCell>
                  <TableCell align="right">Percentuale</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Users in Trial</TableCell>
                  <TableCell align="right">{billingStats.trialUsers}</TableCell>
                  <TableCell align="right">
                    {billingStats.totalUsers > 0
                      ? `${((billingStats.trialUsers / billingStats.totalUsers) * 100).toFixed(1)}%`
                      : '0%'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Utenti Attivi</TableCell>
                  <TableCell align="right">
                    {billingStats.activeUsers}
                  </TableCell>
                  <TableCell align="right">
                    {billingStats.totalUsers > 0
                      ? `${((billingStats.activeUsers / billingStats.totalUsers) * 100).toFixed(1)}%`
                      : '0%'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Utenti Frozen</TableCell>
                  <TableCell align="right">
                    {billingStats.frozenUsers}
                  </TableCell>
                  <TableCell align="right">
                    {billingStats.totalUsers > 0
                      ? `${((billingStats.frozenUsers / billingStats.totalUsers) * 100).toFixed(1)}%`
                      : '0%'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Expired Trials</TableCell>
                  <TableCell align="right">
                    {billingStats.expiredTrials}
                  </TableCell>
                  <TableCell align="right">
                    {billingStats.trialUsers > 0
                      ? `${((billingStats.expiredTrials / billingStats.trialUsers) * 100).toFixed(1)}%`
                      : '0%'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BillingDashboard;
